import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Lock, LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { formatApiErrorDetail } from '@/lib/api';
import { BRAND } from '@/data/content';

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex relative bg-[#0D1B2A] grain overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'url(https://images.pexels.com/photos/17297091/pexels-photo-17297091.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-[#0D1B2A]/80" />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white rounded-sm grid place-items-center overflow-hidden">
              <img src={BRAND.logo} alt="Clan Group" className="w-9 h-9 object-contain" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-display font-bold tracking-tight text-base">
                CLAN GROUP
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
                Admin Console
              </div>
            </div>
          </Link>
          <div>
            <div className="overline mb-5 flex items-center gap-3">
              <span className="gold-rule" /> Restricted Area
            </div>
            <h1 className="font-display text-white text-4xl xl:text-5xl font-bold tracking-tight leading-[1.1]">
              Group operations <span className="text-[#D4AF37]">back-office.</span>
            </h1>
            <p className="mt-5 text-white/70 leading-relaxed max-w-md">
              Review inbound enquiries and career applications across the Clan Group platform.
            </p>
          </div>
          <div className="text-white/40 text-xs uppercase tracking-[0.22em]">
            {BRAND.hq} · {new Date().getFullYear()}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-[#0D1B2A] rounded-sm grid place-items-center overflow-hidden">
              <img src={BRAND.logo} alt="Clan Group" className="w-8 h-8 object-contain" />
            </div>
            <div className="font-display font-bold text-[#0D1B2A]">CLAN GROUP</div>
          </Link>

          <div className="overline mb-4 flex items-center gap-3">
            <span className="gold-rule" /> Admin Sign In
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0D1B2A] tracking-tight">
            Welcome back.
          </h2>
          <p className="mt-3 text-[#4B5563]">Sign in to view enquiries and applications.</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0D1B2A] mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-login-email"
                placeholder="you@clangrp.com"
                className="w-full border border-[#0D1B2A]/15 px-4 py-3 text-[15px] text-[#0D1B2A] focus:border-[#D4AF37] outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0D1B2A] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-login-password"
                  className="w-full border border-[#0D1B2A]/15 px-4 py-3 pr-11 text-[15px] text-[#0D1B2A] focus:border-[#D4AF37] outline-none"
                />
                <Lock
                  size={16}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="text-sm text-red-600 inline-flex items-center gap-2 bg-red-50 border border-red-100 px-4 py-2.5 w-full"
              >
                <AlertCircle size={16} /> {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'} <LogIn size={16} />
            </button>
          </form>

          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 text-[13px] text-[#4B5563] hover:text-[#0D1B2A] transition-colors"
          >
            ← Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}
